/**
 * PaymentProvidersEditionController — page
 * /parametres-encaissement-edition.html
 * (EPIC-099 / 99.13 — §7.I.B + §7.I.H).
 *
 * Refonte 99.13 :
 *  - Sélection d'un provider du catalogue admin (GET /v1/payment-providers/catalog)
 *    au lieu d'un select hardcodé à 6 valeurs.
 *  - Formulaire ME généré dynamiquement à partir de `payment_provider.schemaJson`
 *    (4 types : text / password / url / select).
 *  - Plus de textarea JSON brut côté ME.
 *  - Si `is_oauth=true` (cas SumUp) : flow OAuth dédié, pas de champs à saisir.
 *
 * Modes :
 *  - Création (`?` vide)  → POST /v1/payment-providers
 *  - Édition (`?id=`)     → GET puis PATCH
 *  - Retour OAuth (`?provider=&connected=1`) → traité comme édition + état connecté.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const FIELD_TEMPLATE_BY_TYPE = {
  text: "textFieldTemplate",
  password: "passwordFieldTemplate",
  url: "urlFieldTemplate",
  select: "selectFieldTemplate",
  iban: "textFieldTemplate",
};

const IBAN_LENGTHS = {
  AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28,
  BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
  BY: 28, CH: 21, CR: 22, CY: 28, CZ: 24,
  DE: 22, DK: 18, DO: 28, EE: 20, EG: 29,
  ES: 24, FI: 18, FO: 18, FR: 27, GB: 22,
  GE: 22, GI: 23, GL: 18, GR: 27, GT: 28,
  HR: 21, HU: 28, IE: 22, IL: 23, IS: 26,
  IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28,
  LC: 32, LI: 21, LT: 20, LU: 20, LV: 21,
  MC: 27, MD: 24, ME: 22, MK: 19, MR: 27,
  MT: 31, MU: 30, NL: 18, NO: 15, PK: 24,
  PL: 28, PS: 29, PT: 25, QA: 29, RO: 24,
  RS: 22, SA: 24, SC: 31, SE: 24, SI: 19,
  SK: 24, SM: 27, ST: 25, SV: 28, TL: 23,
  TN: 24, TR: 26, UA: 29, VG: 24, XK: 20,
};

function isValidIban(value) {
  const iban = String(value || "").replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(iban)) return false;
  const country = iban.slice(0, 2);
  if (IBAN_LENGTHS[country] === undefined || iban.length !== IBAN_LENGTHS[country]) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let numeric = "";
  for (const ch of rearranged) {
    numeric += /[A-Z]/.test(ch) ? String(ch.charCodeAt(0) - 55) : ch;
  }
  let remainder = 0;
  for (const digit of numeric) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

export default class PaymentProvidersEditionController extends BaseController {
  static targets = [
    "pageTitle",
    "catalogSelect",
    "catalogHint",
    "labelFieldset",
    "labelInput",
    "isActiveInput",
    "dynamicFormFieldset",
    "dynamicFormContainer",
    "oauthBlock",
    "oauthProviderLabel",
    "oauthDescription",
    "oauthConnectBtn",
    "oauthConnectLabel",
    "oauthHint",
    "oauthNotConnected",
    "oauthConnected",
    "sumupAckWrapper",
    "sumupAck",
    "sumupMerchantCode",
    "sumupStatus",
    "sumupQrDownload",
    "errorBanner",
    "submitBtn",
    "submitActions",
    "textFieldTemplate",
    "passwordFieldTemplate",
    "urlFieldTemplate",
    "selectFieldTemplate",
  ];

  async connect() {
    this.mode = "create";
    this.editedId = null;
    this.catalog = [];
    this.selectedProvider = null;
    await this.loadCatalog();
    await this.applyQueryString();
  }

  // ===========================================================================
  // Setup
  // ===========================================================================

  async loadCatalog() {
    try {
      const items = await api.paymentProviders.catalog();
      this.catalog = Array.isArray(items) ? items : [];
      this.populateCatalogSelect();
      this.catalogHintTarget.textContent = this.catalog.length === 0
        ? "Aucun moyen de paiement disponible dans le catalogue."
        : "Le catalogue est défini par l'équipe Notamicro.";
    } catch (error) {
      this.catalogHintTarget.textContent = "Impossible de charger le catalogue.";
      this.handleApiError(error, "Impossible de charger le catalogue.");
    }
  }

  populateCatalogSelect() {
    const select = this.catalogSelectTarget;
    for (const option of Array.from(select.querySelectorAll("option"))) {
      if (option.value !== "") option.remove();
    }
    for (const provider of this.catalog) {
      const opt = document.createElement("option");
      opt.value = provider.id;
      opt.textContent = provider.label;
      opt.dataset.code = provider.code;
      select.appendChild(opt);
    }
  }

  async applyQueryString() {
    const qs = new URLSearchParams(window.location.search);
    const id = qs.get("id") || qs.get("instance");
    const oauthCallbackId = qs.get("provider");
    const connectedFlag = qs.get("connected");
    if (id || oauthCallbackId) {
      this.mode = "update";
      this.editedId = id || oauthCallbackId;
      this.pageTitleTarget.textContent = "Modifier un moyen de paiement";
      await this.loadInstance(this.editedId);
      if (connectedFlag === "1") {
        this.showOauthConnectedState();
      }
    }
  }

  async loadInstance(id) {
    try {
      const response = await api.paymentProviders.get(id);
      const instance = response?.data ?? null;
      if (!instance) {
        this.showErrorBanner("Moyen de paiement introuvable.");
        return;
      }
      this.fillFromInstance(instance);
    } catch (error) {
      this.handleApiError(error, "Impossible de charger ce moyen de paiement.");
    }
  }

  fillFromInstance(instance) {
    const provider = this.catalog.find((p) => p.id === instance.paymentProviderId) ?? null;
    this.selectedProvider = provider;
    this.catalogSelectTarget.value = instance.paymentProviderId;
    this.catalogSelectTarget.disabled = true;
    this.labelFieldsetTarget.hidden = false;
    this.labelInputTarget.value = instance.label ?? "";
    this.isActiveInputTarget.checked = instance.isActive !== false;
    this.submitActionsTarget.hidden = false;
    if (provider) {
      this.refreshFormForProvider(provider, instance);
    }
  }

  // ===========================================================================
  // Catalog change → render dynamic form or OAuth block
  // ===========================================================================

  onCatalogChange() {
    const id = this.catalogSelectTarget.value;
    if (id === "") {
      this.selectedProvider = null;
      this.labelFieldsetTarget.hidden = true;
      this.dynamicFormFieldsetTarget.hidden = true;
      this.oauthBlockTarget.hidden = true;
      this.submitActionsTarget.hidden = true;
      return;
    }
    const provider = this.catalog.find((p) => p.id === id) ?? null;
    if (!provider) return;
    this.selectedProvider = provider;
    this.labelFieldsetTarget.hidden = false;
    if (this.labelInputTarget.value.trim() === "") {
      this.labelInputTarget.value = provider.label;
    }
    this.refreshFormForProvider(provider, null);
  }

  refreshFormForProvider(provider, instance) {
    if (provider.isOauth) {
      this.dynamicFormFieldsetTarget.hidden = true;
      this.renderOauthBlock(provider, instance);
    } else {
      this.oauthBlockTarget.hidden = true;
      this.renderDynamicForm(provider, instance);
    }
    this.submitActionsTarget.hidden = false;
  }

  renderDynamicForm(provider, instance) {
    const fieldset = this.dynamicFormFieldsetTarget;
    const container = this.dynamicFormContainerTarget;
    container.innerHTML = "";
    const fields = Array.isArray(provider.schemaJson) ? provider.schemaJson : [];
    if (fields.length === 0) {
      fieldset.hidden = true;
      return;
    }
    fieldset.hidden = false;
    const existingValues = instance?.configValues ?? {};
    for (const field of fields) {
      const templateName = FIELD_TEMPLATE_BY_TYPE[field.type] ?? "textFieldTemplate";
      const targetName = `${templateName}Target`;
      const template = this[targetName];
      if (!template) continue;
      const fragment = this.cloneTemplate(template);
      const root = fragment.querySelector("[data-dynamic-field]");
      if (!root) continue;
      root.dataset.fieldName = field.name;
      root.dataset.fieldType = field.type;
      const labelEl = root.querySelector('[data-slot="label"]');
      const hintEl = root.querySelector('[data-slot="hint"]');
      const input = root.querySelector("[data-dynamic-input]");
      if (labelEl) {
        labelEl.textContent = field.label;
        if (field.required) {
          const star = document.createElement("span");
          star.setAttribute("aria-hidden", "true");
          star.textContent = " *";
          labelEl.appendChild(star);
        }
      }
      if (hintEl) {
        if (field.hint && field.hint !== "") {
          hintEl.textContent = field.hint;
          hintEl.hidden = false;
        } else {
          hintEl.hidden = true;
        }
      }
      if (input) {
        if (field.required) input.required = true;
        if (field.type === "select" && Array.isArray(field.options)) {
          for (const opt of field.options) {
            const option = document.createElement("option");
            option.value = opt.value ?? "";
            option.textContent = opt.label ?? opt.value ?? "";
            input.appendChild(option);
          }
        }
        const existing = existingValues[field.name];
        if (existing !== undefined && existing !== null) {
          input.value = String(existing);
        }
      }
      container.appendChild(fragment);
    }
  }

  renderOauthBlock(provider, instance) {
    this.oauthBlockTarget.hidden = false;
    this.oauthProviderLabelTarget.textContent = provider.label;
    this.oauthConnectLabelTarget.textContent = provider.label;
    this.oauthDescriptionTarget.textContent = provider.description
      ?? `Connecter ${provider.label} permet à Notamicro de récupérer automatiquement vos encaissements.`;
    const isSumup = provider.code === "sumup";
    this.sumupAckWrapperTarget.hidden = !isSumup;
    if (!isSumup) {
      this.oauthConnectBtnTarget.disabled = false;
      this.oauthHintTarget.hidden = true;
    } else {
      this.oauthConnectBtnTarget.disabled = true;
      this.oauthHintTarget.hidden = false;
    }
    if (instance && instance.oauthState && typeof instance.oauthState.merchant_code === "string"
        && instance.oauthState.merchant_code !== "") {
      this.showOauthConnectedState(instance);
    } else {
      this.oauthNotConnectedTarget.hidden = false;
      this.oauthConnectedTarget.hidden = true;
    }
  }

  showOauthConnectedState(instance) {
    this.oauthNotConnectedTarget.hidden = true;
    this.oauthConnectedTarget.hidden = false;
    if (instance?.oauthState?.merchant_code) {
      this.sumupMerchantCodeTarget.textContent = instance.oauthState.merchant_code;
    }
    if (instance) {
      this.sumupStatusTarget.textContent = instance.isActive ? "Actif" : "Inactif";
    }
    if (this.editedId) {
      this.sumupQrDownloadTarget.href = api.sumup.qrUrl(this.editedId);
    }
  }

  onSumupAckChange() {
    this.oauthConnectBtnTarget.disabled = !this.sumupAckTarget.checked;
  }

  async onOauthConnect(event) {
    if (event) event.preventDefault();
    const provider = this.selectedProvider;
    if (!provider) return;
    if (provider.code === "sumup" && !this.sumupAckTarget.checked) {
      this.showErrorBanner("Cochez la case d'acquittement avant de continuer.");
      return;
    }
    this.hideErrorBanner();
    this.oauthConnectBtnTarget.disabled = true;
    try {
      const response = await api.sumup.oauthInit(true);
      const url = response?.data?.url;
      if (typeof url === "string" && url !== "") {
        window.location.assign(url);
      } else {
        this.showErrorBanner("Réponse OAuth invalide — réessayez plus tard.");
      }
    } catch (error) {
      this.handleApiError(error, "Impossible de joindre le prestataire — réessayez plus tard.");
    } finally {
      this.oauthConnectBtnTarget.disabled = provider.code === "sumup" && !this.sumupAckTarget.checked;
    }
  }

  // ===========================================================================
  // Submit
  // ===========================================================================

  async submit(event) {
    if (event) event.preventDefault();
    this.hideErrorBanner();
    this.clearFieldErrors();
    const provider = this.selectedProvider;
    if (!provider) {
      this.showErrorBanner("Sélectionnez un type de moyen de paiement.");
      return;
    }
    if (provider.isOauth) {
      // Les instances OAuth se créent via le flow d'autorisation, pas via submit.
      this.showErrorBanner("Utilisez le bouton « Continuer vers " + provider.label + " ».");
      return;
    }
    const label = this.labelInputTarget.value.trim();
    if (label === "") {
      this.showErrorBanner("Le libellé est requis.");
      this.markFieldInvalid("instanceLabel");
      return;
    }
    const configValues = this.collectConfigValues();
    const ibanErrors = this.validateIbanFields(configValues);
    if (ibanErrors.length > 0) {
      this.applyFieldErrors(ibanErrors);
      return;
    }
    const payload = {
      paymentProviderId: provider.id,
      label,
      configValues,
      isActive: this.isActiveInputTarget.checked,
    };
    this.submitBtnTarget.disabled = true;
    try {
      if (this.mode === "update" && this.editedId) {
        const { paymentProviderId: _id, ...patch } = payload;
        await api.paymentProviders.update(this.editedId, patch);
      } else {
        await api.paymentProviders.create(payload);
      }
      window.location.assign("/parametres-encaissement.html");
    } catch (error) {
      this.handleApiError(error);
    } finally {
      this.submitBtnTarget.disabled = false;
    }
  }

  collectConfigValues() {
    const out = {};
    const fields = this.dynamicFormContainerTarget.querySelectorAll("[data-dynamic-field]");
    for (const field of fields) {
      const name = field.dataset.fieldName;
      const input = field.querySelector("[data-dynamic-input]");
      if (!name || !input) continue;
      const value = input.value;
      if (value !== undefined && value !== null && value !== "") {
        out[name] = String(value);
      }
    }
    return out;
  }

  validateIbanFields(configValues) {
    const errors = [];
    const fields = this.dynamicFormContainerTarget.querySelectorAll(
      "[data-dynamic-field][data-field-type='iban']",
    );
    for (const field of fields) {
      const name = field.dataset.fieldName;
      if (!name) continue;
      const value = configValues[name];
      if (value === undefined || value === "") continue;
      if (!isValidIban(value)) {
        errors.push({
          field: `config_values.${name}`,
          code: "invalid_iban",
          message: "IBAN invalide.",
        });
      }
    }
    return errors;
  }

  // ===========================================================================
  // Errors
  // ===========================================================================

  handleApiError(error, fallback) {
    if (error instanceof APIError) {
      if (error.status === 422) {
        const fields = error.data?.error?.details?.fields ?? error.data?.fields;
        if (Array.isArray(fields) && fields.length > 0) {
          this.applyFieldErrors(fields);
          return;
        }
      }
      this.showErrorBanner(error.message || fallback || "Erreur — impossible d'enregistrer.");
      return;
    }
    this.showErrorBanner(fallback || "Erreur réseau — impossible d'enregistrer.");
  }

  applyFieldErrors(fields) {
    for (const f of fields) {
      if (f?.field) this.markFieldInvalid(f.field);
    }
    const messages = fields.map((f) => f?.message).filter((m) => typeof m === "string" && m !== "");
    this.showErrorBanner(messages.length > 0 ? messages.join(" — ") : "Certains champs sont invalides.");
  }

  markFieldInvalid(fieldName) {
    const target = this.element.querySelector(
      `[name="${fieldName}"], #${fieldName}, [data-field-name="${fieldName.replace(/^config_values\./, "")}"] [data-dynamic-input]`,
    );
    if (target) target.setAttribute("aria-invalid", "true");
  }

  clearFieldErrors() {
    this.element.querySelectorAll("[aria-invalid='true']").forEach((el) => {
      el.removeAttribute("aria-invalid");
    });
  }

  showErrorBanner(message) {
    this.errorBannerTarget.textContent = message;
    this.errorBannerTarget.hidden = false;
  }

  hideErrorBanner() {
    this.errorBannerTarget.hidden = true;
    this.errorBannerTarget.textContent = "";
  }
}
